import { z } from 'zod';

export const REPORT_ARTIFACT_SCHEMA_VERSION = 2;
export const REPORT_ARTIFACT_FORMATS = Object.freeze(['repx']);
export const REPORT_ARTIFACT_ACCESS_POLICIES = Object.freeze([
    'source-current-v1',
    'controlled-recipient-v1',
    'legacy-sidecar-v1',
]);

const id = z.string().trim().min(1).max(200);
const stableKey = z.string().trim().min(1).max(120).regex(/^[a-z][a-z0-9-]*$/);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const date = z.coerce.date();

export const ReportArtifactFileSchema = z.object({
    format: z.enum(REPORT_ARTIFACT_FORMATS),
    resourceId: id,
    storageVersionId: id,
    etag: z.string().trim().min(1).max(500),
    contentType: z.string().trim().min(1).max(160),
    size: z.number().int().positive().max(1_073_741_824),
    sha256,
    verifiedAt: date,
    operationId: id,
}).strict();

const publicationSchema = z.object({
    state: z.enum(['pending', 'bytes-written', 'checksum-verified', 'metadata-committed', 'published', 'approved', 'failed', 'reconciling']),
    operationId: id,
    requestedAt: date,
    bytesWrittenAt: date.optional(),
    checksumVerifiedAt: date.optional(),
    metadataCommittedAt: date.optional(),
    publishedAt: date.optional(),
    approvedAt: date.optional(),
    failedAt: date.optional(),
    failureStage: z.string().trim().min(1).max(120).optional(),
    failureCode: z.string().trim().min(1).max(200).optional(),
}).strict();

const deliverySchema = z.object({
    userId: id,
    delivery: z.string().trim().min(1).max(120),
    status: z.enum(['pending', 'sent', 'delivered', 'failed', 'cancelled']),
    attempts: z.number().int().nonnegative().max(1000),
    channels: z.array(z.string().trim().min(1).max(80)).max(20),
}).strict();

const controlledLifecycleSchema = z.object({
    documentId: id.nullable(),
    documentRevisionId: id.nullable(),
    status: z.enum(['draft', 'review', 'approved', 'cancelled']),
    approvedBy: id.optional(),
    approvedAt: date.optional(),
    cancelledBy: id.optional(),
    cancelledAt: date.optional(),
    lifecycleComment: z.string().max(4000).optional(),
    deliveries: z.array(deliverySchema).max(10_000),
    tombstone: z.object({ at: date, by: id, reason: z.string().trim().min(1).max(1000) }).strict().nullable(),
    deletion: z.object({
        state: z.enum(['none', 'requested', 'deleting', 'completed', 'failed']),
        operationId: id.optional(),
        requestedBy: id.optional(),
        requestedAt: date.optional(),
        completedAt: date.optional(),
        failedAt: date.optional(),
        failureCode: z.string().trim().min(1).max(120).optional(),
    }).strict(),
}).strict();

export const ReportArtifactSchema = z.object({
    schemaVersion: z.literal(REPORT_ARTIFACT_SCHEMA_VERSION),
    tenant: id,
    reportId: id,
    revision: z.number().int().positive(),
    idempotencyKey: id,
    description: z.string().max(2000).optional(),
    reportKey: stableKey,
    definitionVersion: z.number().int().positive(),
    composition: z.object({
        moduleKey: stableKey,
        moduleVersion: z.number().int().positive(),
        documentSpecVersion: z.number().int().positive(),
    }).strict(),
    kind: z.enum(['interactive', 'scheduled-controlled', 'legacy-converted']),
    source: z.object({
        surfaceKey: stableKey,
        scope: z.record(z.string(), z.unknown()),
        scopeDigest: sha256,
        selectorDigest: sha256,
        bindingDigest: sha256,
        jobId: id.optional(),
        snapshotId: id.optional(),
        baselineSnapshotId: id.optional(),
        dataSnapshotTime: date.optional(),
        legacyEntityType: z.string().trim().min(1).max(120).optional(),
        legacyEntityId: id.optional(),
    }).strict(),
    profile: z.object({
        id,
        version: z.number().int().positive(),
        contractKey: stableKey,
        contractVersion: z.number().int().positive(),
        digest: sha256,
    }).strict().nullable(),
    branding: z.object({
        revisionId: id,
        version: z.number().int().positive(),
        digest: sha256,
    }).strict(),
    generatedBy: id.nullable(),
    generatedAt: date,
    artifacts: z.array(ReportArtifactFileSchema).min(1).max(REPORT_ARTIFACT_FORMATS.length)
        .refine((items) => new Set(items.map((item) => item.format)).size === items.length, {
            message: 'Artifact formats must be unique',
        }),
    publication: publicationSchema,
    replacementPublication: publicationSchema.nullable(),
    controlledLifecycle: controlledLifecycleSchema.nullable(),
    accessPolicy: z.enum(REPORT_ARTIFACT_ACCESS_POLICIES),
    retentionUntil: date.nullable(),
    legalHold: z.object({
        active: z.boolean(),
        reason: z.string().trim().min(1).max(1000).optional(),
        placedBy: id.optional(),
        placedAt: date.optional(),
        releasedBy: id.optional(),
        releasedAt: date.optional(),
    }).strict(),
    legacySource: z.record(z.string(), z.unknown()).optional(),
    customFields: z.record(z.string().trim().min(1).max(120), z.unknown()).default({}),
    createdAt: date,
    updatedAt: date,
}).strict().superRefine((value, ctx) => {
    const isFinalPublication = ['published', 'approved'].includes(value.publication.state);
    if (value.schemaVersion === 2 && isFinalPublication
        && (value.artifacts.length !== 1 || value.artifacts[0]?.format !== 'repx')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['artifacts'],
            message: 'Published version 2 report artifacts require exactly one REPX output',
        });
    }
    if (value.kind !== 'legacy-converted' && !value.generatedBy) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['generatedBy'], message: 'New artifacts require a generator' });
    }
    if (value.kind === 'scheduled-controlled' && !value.controlledLifecycle) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['controlledLifecycle'], message: 'Controlled artifacts require lifecycle evidence' });
    }
});

export const ReportArtifactArchiveGrantClaimsSchema = z.object({
    jti: id,
    tenantId: id,
    userId: id,
    sessionId: sha256,
    reportKey: stableKey,
    definitionVersion: z.number().int().positive(),
    bindingDigest: sha256,
    documentDigest: sha256,
    brandingDigest: sha256,
    archiveContractVersion: z.literal(2),
    archiveFormat: z.literal('repx'),
    exp: z.number().int().positive(),
}).strict();

export const ReportArtifactCreateSchema = z.object({
    archiveGrant: z.string().trim().min(1).max(8192),
    idempotencyKey: id,
    description: z.string().max(2000).optional(),
}).strict();

export const ReportArtifactLifecycleUpdateSchema = z.object({
    expectedRevision: z.number().int().positive(),
    action: z.enum(['submit-review', 'approve', 'cancel', 'delete', 'release-hold']),
    comment: z.string().trim().min(1).max(4000).optional(),
}).strict();
